import React, { useCallback } from 'react';
import Heading from '@atlaskit/heading';
import CreditcardFilledIcon from '@atlaskit/icon/core/migration/credit-card--creditcard-filled';
import PremiumIcon from '@atlaskit/icon/core/migration/premium';
import RecentIcon from '@atlaskit/icon/core/migration/clock--recent';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	PREMIUM_REVERSE_TRIAL_TASK,
	ADVANCED_ROADMAPS_TASK,
} from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import { useJswOnboarding } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { useOpenChecklist } from '@atlassian/jira-software-onboarding-quickstart/src/controllers/open-checklist/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useIsTailoredOnboardingEnabledAndEligible } from '@atlassian/jira-tailored-onboarding/src/controllers/index.tsx';
import { ChangePlansUrlProvider } from './change-plans-url-provider/index.tsx';
import { ListItem } from './list-item/index.tsx';
import messages from './messages.tsx';

export type Props = {
	billingPageUrl: string;
	onFireUiAnalyticsEvent: (
		action: string,
		actionSubject: string,
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => void;
};

export const ReverseTrialExperimentPopupContent = ({
	billingPageUrl,
	onFireUiAnalyticsEvent,
}: Props) => {
	const { formatMessage } = useIntl();
	const openChecklist = useOpenChecklist();
	const [{ currentTaskGroupParent }, { changeTaskGroupParent, nextOpenTask }] = useJswOnboarding();

	const {
		isReady,
		isTailoredOnboardingM3EnabledAndEligible,
		isTailoredOnboardingScrumAndSoftwareKanbanEnabled,
	} = useIsTailoredOnboardingEnabledAndEligible();
	const isHidePremiumFeatureMenuItem =
		isReady &&
		(isTailoredOnboardingM3EnabledAndEligible ||
			isTailoredOnboardingScrumAndSoftwareKanbanEnabled ||
			expValEquals('jira_rt_quickstart_m1', 'isEnabled', true));

	const onClickExplorePremiumFeatures = useCallback(() => {
		onFireUiAnalyticsEvent('clicked', 'button', 'trialPillExplorePremium');
		const taskGroupToEnter = PREMIUM_REVERSE_TRIAL_TASK;

		if (currentTaskGroupParent !== taskGroupToEnter) {
			changeTaskGroupParent(taskGroupToEnter);
		}

		openChecklist({
			trigger: 'ReverseTrialPopup',
		});

		nextOpenTask(ADVANCED_ROADMAPS_TASK, {
			closeNudge: true,
		});
	}, [
		onFireUiAnalyticsEvent,
		currentTaskGroupParent,
		openChecklist,
		nextOpenTask,
		changeTaskGroupParent,
	]);

	const onLinkClick = useCallback(
		(title: string) => {
			onFireUiAnalyticsEvent('clicked', 'button', title);
		},
		[onFireUiAnalyticsEvent],
	);

	return (
		<>
			<Heading size="small">{formatMessage(messages.title)}</Heading>
			<Stack as="ul" xcss={listContainerStyles} alignInline="stretch">
				{!isHidePremiumFeatureMenuItem ? (
					<ListItem
						title={formatMessage(messages.explorePremiumFeaturesLink)}
						onClick={onClickExplorePremiumFeatures}
						icon={<PremiumIcon label="" LEGACY_size="small" />}
					/>
				) : null}
				<ListItem
					title={formatMessage(messages.addPaymentDetailsLink)}
					link={billingPageUrl}
					onClick={() => onLinkClick('trialPillAddPaymentDetails')}
					icon={<CreditcardFilledIcon label="" LEGACY_size="small" />}
				/>
				<ChangePlansUrlProvider>
					{(changePlansUrl) =>
						changePlansUrl ? (
							<ListItem
								title={formatMessage(messages.endTrialAndChangePlansLink)}
								link={changePlansUrl}
								onClick={() => onLinkClick('trialPillChangePlans')}
								icon={<RecentIcon label="" LEGACY_size="small" />}
							/>
						) : null
					}
				</ChangePlansUrlProvider>
			</Stack>
			<Box as="p" xcss={downgradeTextStyles}>
				{formatMessage(messages.afterTrial)}
			</Box>
		</>
	);
};

const listContainerStyles = xcss({
	listStyle: 'none',
	marginTop: 'space.100',
	padding: 'space.0',
});

const downgradeTextStyles = xcss({
	font: token('font.body.UNSAFE_small'),
	color: 'color.text.subtle',
});
